import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import axios from 'axios';
import { usePublicUiLocale } from '../../contexts/PublicUiLocaleContext';
import ProjectCostSummary from './ProjectCostSummary';
import { loadCart } from './storeCart';
import { buildStoreUrl } from './storeUrls';
import { useAdminPanelTheme } from './AdminPanelThemeContext';

function getAuthToken() {
  try {
    return localStorage.getItem('token') || sessionStorage.getItem('token') || '';
  } catch {
    return '';
  }
}

/** Compact billing estimate for the Project tab in superadmin. */
export default function ProjectStoreCostWidget({ projectSlug, returnTo }) {
  const navigate = useNavigate();
  const { moduleStore: ms } = usePublicUiLocale();
  const { mode: themeMode } = useAdminPanelTheme();
  const [catalog, setCatalog] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const cart = loadCart(projectSlug);

  const loadCatalog = useCallback(async () => {
    if (!projectSlug) {
      setCatalog(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const token = getAuthToken();
      const res = await axios.get(
        `${window.location.origin}/api/p/${projectSlug}/auth/store-catalog`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setCatalog(res?.data || null);
    } catch {
      setCatalog(null);
      setError(ms.projectCostLoadFailed);
    } finally {
      setLoading(false);
    }
  }, [projectSlug, ms.projectCostLoadFailed]);

  useEffect(() => {
    loadCatalog();
  }, [loadCatalog]);

  if (!projectSlug) return null;

  if (loading && !catalog) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 1.5, mb: 1 }}>
        <CircularProgress size={22} sx={{ color: 'text.secondary' }} />
      </Box>
    );
  }

  if (error && !catalog) {
    return (
      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 2 }}>
        {error}
      </Typography>
    );
  }

  if (!catalog?.entitlements) return null;

  const storeUrl = buildStoreUrl(projectSlug, { returnTo });

  return (
    <ProjectCostSummary
      catalog={catalog}
      entitlements={catalog.entitlements}
      cart={cart}
      locale={ms}
      context="admin"
      variant="compact"
      themeMode={themeMode}
      onOpenStore={() => navigate(storeUrl)}
    />
  );
}

ProjectStoreCostWidget.propTypes = {
  projectSlug: PropTypes.string,
  returnTo: PropTypes.string,
};
